import * as React from 'react';
import { Component, ComponentClass, Omit } from 'react-redux';

/**
 * This interface allows each instance of the HOC to choose a custom name for its "fetch data" prop, but in a type-safe way.
 * For example, FetchDataInChildProp<'foo'> would have a prop called 'foo' on the wrapped component.
 */
export type FetchDataInChildProp<PropName extends string> = {
  [Key in PropName]: () => void;
};

export type FetchDataInWrapper<OwnProps, AsyncData extends Object> = (props: OwnProps) => Promise<AsyncData>;

export type AsyncStoreWrappedComponentProps<OwnProps, AsyncData extends Object, PropName extends string> = OwnProps & Partial<AsyncData> & FetchDataInChildProp<PropName>;
export type AsyncStoreWrappedComponent<OwnProps, AsyncData extends Object, PropName extends string> = React.ComponentType<AsyncStoreWrappedComponentProps<OwnProps, AsyncData, PropName>>;

export type AsyncStoreContainerHOC<OwnProps, AsyncData extends Object, PropName extends string> =
  (wrappedComponent: AsyncStoreWrappedComponent<OwnProps, AsyncData, PropName>) => React.ComponentClass<OwnProps>;


export interface AsyncStoreHOCParams<OwnProps, AsyncData extends Object, PropName extends string> {

  /** The function that will fetch the data to reside in the store */
  fetchDataFunction: FetchDataInWrapper<OwnProps, AsyncData>;

  /** The name of the prop passed to the wrapped component that allows it to initiate the data fetch */
  internalPropName: PropName;
}

/**
 * This higher-order component wraps a child component in a store than can fetch data asynchronously
 * and then pass it back down into the component. This pattern can be used as an alternative to Redux
 * where the fetched data doesn't need to be stored globally.
 *
 * @param {AsyncStoreHOCParams<OwnProps, AsyncData extends Object, PropName extends string>} params
 * @returns {AsyncStoreContainerHOC<OwnProps, AsyncData extends Object, PropName extends string>}
 *          A function that will wrap an inner component with the store container component
 */
export function asyncStoreContainerHOC<OwnProps, AsyncData extends Object, PropName extends string>(
  params: AsyncStoreHOCParams<OwnProps, AsyncData, PropName>
): AsyncStoreContainerHOC<OwnProps, AsyncData, PropName> {

  return wrappedComponent => {

    return class AsyncStoreContainerComponent extends React.Component<OwnProps, AsyncData> {

      private _isMounted: boolean = false;

      private fetchAndStoreAsyncData = async () => {
        const data = await params.fetchDataFunction(this.props);
        if (this._isMounted) {
          this.setState(data);
        }
      }

      public componentDidMount(): void {
        this._isMounted = true;
      }

      public componentWillUnmount(): void {
        this._isMounted = false;
      }

      public render(): JSX.Element {

        const fetchDataProp = {
          [params.internalPropName]: this.fetchAndStoreAsyncData,
        } as unknown as FetchDataInChildProp<PropName>;

        const propsForChild: AsyncStoreWrappedComponentProps<OwnProps, AsyncData, PropName> = {
          ...this.props,
          ...this.state,
          ...fetchDataProp,
        };

        return React.createElement(wrappedComponent, propsForChild);
      }
    };
  };

}
